import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { GET_CAMPAIGN, GET_PITCH_SLIDE } from '../graphql/queries';
import { WpLead } from '../contexts/lead';
import { PRO_WIDGET_LABEL, getProInstrumentLabel } from './proWidget';
import { formatApolloError } from './apolloError';

export interface CampaignProWidget {
  widgetId: string;
  label: string;
  campaignId: string;
  campaignName: string;
  location: 'landing' | 'pitch';
}

interface SlideWidget {
  id?: string;
  widgetType?: string;
  label?: string;
  labels?: string[];
}

export function isProSlideWidget(widget: { widgetType?: string; label?: string | null; proInstrument?: string | null }): boolean {
  return widget.widgetType === 'PRO'
    || widget.proInstrument === 'ASES'
    || widget.proInstrument === 'OKS'
    || widget.proInstrument === 'OHS'
    || widget.label === PRO_WIDGET_LABEL
    || !!widget.label?.toLowerCase().includes('ases shoulder')
    || !!widget.label?.toLowerCase().includes('oxford knee')
    || !!widget.label?.toLowerCase().includes('oxford hip');
}

export function collectPitchSlideIds(campaign: {
  features?: { pitch?: { structure?: { id?: string }[] } }[];
  structure?: {
    feature?: { structure?: { id?: string }[] };
    before?: { structure?: { id?: string }[] };
    appointment?: { structure?: { id?: string }[] };
  }[];
}): string[] {
  const ids: string[] = [];
  const addSlides = (structure?: { id?: string }[]) => {
    for (const slide of structure ?? []) {
      if (slide.id && slide.id !== 'redirect' && !ids.includes(slide.id)) {
        ids.push(slide.id);
      }
    }
  };

  for (const feature of campaign.features ?? []) {
    addSlides(feature.pitch?.structure);
  }
  for (const item of campaign.structure ?? []) {
    addSlides(item.feature?.structure);
    addSlides(item.before?.structure);
    addSlides(item.appointment?.structure);
  }
  return ids;
}

function mapSlideWidgets(
  widgets: SlideWidget[],
  campaignId: string,
  campaignName: string,
  location: 'landing' | 'pitch',
): CampaignProWidget[] {
  return widgets
    .filter((w) => w.id && isProSlideWidget(w))
    .map((w) => ({
      widgetId: w.id!,
      label: getProInstrumentLabel(w.labels?.[0]) || w.label || PRO_WIDGET_LABEL,
      campaignId,
      campaignName,
      location,
    }));
}

/** List PRO widgets on a campaign (landing page + pitch slides). Does not create widgets. */
export async function fetchCampaignProWidgets(
  client: ApolloClient<NormalizedCacheObject>,
  campaignId: string,
  campaignName = `Campaign ${campaignId}`,
): Promise<CampaignProWidget[]> {
  const { data } = await client.query({
    query: GET_CAMPAIGN,
    variables: { campaignId },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  const campaign = data?.campaign?.campaign;
  if (!campaign) {
    return [];
  }

  const name = campaign.name ?? campaignName;
  const options: CampaignProWidget[] = mapSlideWidgets(
    campaign.landingPage?.widgets ?? [],
    campaignId,
    name,
    'landing',
  );

  const slideIds = collectPitchSlideIds(campaign);
  for (const slideId of slideIds) {
    const { data: slideData } = await client.query({
      query: GET_PITCH_SLIDE,
      variables: { pitchSlideId: slideId },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    });
    options.push(
      ...mapSlideWidgets(
        slideData?.pitchSlide?.slide?.widgets ?? [],
        campaignId,
        name,
        'pitch',
      ),
    );
  }

  const seen = new Set<string>();
  return options.filter((option) => {
    const key = `${option.campaignId}:${option.widgetId}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

/** PRO widgets across campaigns linked to this lead. */
export async function fetchLeadProWidgetOptions(
  client: ApolloClient<NormalizedCacheObject>,
  lead: WpLead,
): Promise<CampaignProWidget[]> {
  const options: CampaignProWidget[] = [];
  const seenCampaigns = new Set<string>();

  for (const enrollment of lead.campaigns ?? []) {
    const campaignId = enrollment.campaign?.id;
    if (!campaignId || seenCampaigns.has(campaignId)) {
      continue;
    }
    seenCampaigns.add(campaignId);
    try {
      const widgets = await fetchCampaignProWidgets(
        client,
        campaignId,
        enrollment.campaign?.name ?? `Campaign ${campaignId}`,
      );
      options.push(...widgets);
    } catch {
      // skip campaigns that fail to load
    }
  }

  if (options.length) {
    return options;
  }

  const fallbackCampaignId = lead.messages.find((m) => m.campaignId)?.campaignId
    ?? lead.activity.find((a) => a.campaignId)?.campaignId;

  if (fallbackCampaignId && !seenCampaigns.has(fallbackCampaignId)) {
    try {
      return await fetchCampaignProWidgets(client, fallbackCampaignId);
    } catch (error) {
      if (__DEV__) {
        console.warn('fetchLeadProWidgetOptions fallback failed', formatApolloError(error));
      }
    }
  }

  return [];
}

export function formatProWidgetOptionLabel(option: CampaignProWidget): string {
  const locationLabel = option.location === 'landing' ? 'Landing page' : 'Pitch';
  return `${option.campaignName} — ${option.label} (${locationLabel})`;
}
